import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { clone, includes, omit } from 'lodash-es';
import { FormPageTypeEnum, PreviewPageType, TemplateScopeType, TemplateType } from '@dev/ee-api/ee-api-models';
import { Typography } from '@dev/ee-components';

import { ModifyForm, getFormCreatorStore, SetFormErrors } from '@/store/formCreator/actions';
import { StartLoadingAlreadySubscribedLandingPages, StartLoadingTemplateAlreadySubscribed, StartLoadingTemplateParams, getLandingPageList } from '@/store/landingPageList/actions';
import { PageTypeEnum } from '@/store/landingPageList/types';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import { FormErrorNames } from '@/store/formCreator/types';

import { Divider } from '@/assets/Divider';
import { usePageTypeValue } from '../hooks/usePageValue';
import { DisplayedScreenEnum, FormPageDropdownSection } from '../components/FormPageDropdown';
import { DEFAULT_OPTIONS_COUNT } from '..';

export const AlreadySubscribedDropdown = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { formObject, formPayload, errors } = useSelector(getFormCreatorStore);
    const { templateAlreadySubscribed, alreadySubscribedLandingPagesList } = useSelector(getLandingPageList);
    const [chosenLandingPage, setChosenLandingPage] = useState<any>([]);
    const [landingPageSearchAlreadySubmitedForm, setLandingPageSearchAlreadySubmitedForm] = useState(null);

    const alreadySubscribedPage = formPayload?.FormAlreadySubscribedPage ?? formObject?.FormAlreadySubscribedPage;
    const [externalWebsite, setExternalWebsite] = useState(!checkIsUrlInvalid(alreadySubscribedPage) ? alreadySubscribedPage : null);
    const [isExternalWebsiteError, setExternalWebsiteError] = useState(false);

    const alreadySubscribedPagesParams: StartLoadingTemplateParams = {
        page: 1,
        search: landingPageSearchAlreadySubmitedForm,
        limit: DEFAULT_OPTIONS_COUNT,
        type: [TemplateScopeType.Personal],
        templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
        isInfinityScroll: true,
        pageType: PageTypeEnum.AlreadySubscribed
    };

    useEffect((): void => {
        dispatch(StartLoadingAlreadySubscribedLandingPages(alreadySubscribedPagesParams));
        dispatch(ModifyForm({FormAlreadySubscribedPage: formObject?.FormAlreadySubscribedPage || null}));

        if (includes(formObject?.FormPageType, FormPageTypeEnum.LandingPageFormSubscribedPage) && !!formObject?.FormAlreadySubscribedPage) {
            dispatch(StartLoadingTemplateAlreadySubscribed(formObject?.FormAlreadySubscribedPage));
        }
    },[]);

    useEffect((): void => {
        setChosenLandingPage({label: templateAlreadySubscribed?.Name, value: templateAlreadySubscribed?.Name, link: templateAlreadySubscribed?.Link});
    }, [templateAlreadySubscribed]);

    const errorName = FormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL;

    const resetError = (): void => {
        const errorsObject = clone(errors) || {};
        const newErrorsObject = omit(errorsObject, errorName);
        dispatch(SetFormErrors(newErrorsObject));
    }

    useEffect(() => {
        const errorsObject = clone(errors) || {};
        
        if (isExternalWebsiteError) {
            errorsObject[errorName] = true;
            dispatch(SetFormErrors(errorsObject));
            return;
        }
        resetError();
    }, [isExternalWebsiteError]);

    const changeAlreadySubscribedPage = (e, obj): void => {
        const formPageType = formPayload?.FormPageType ?? formObject?.FormPageType;
        const newFormPageType = [...formPageType];
        let newAlreadySubscribedPage = null;

        if (obj.value === DisplayedScreenEnum.Defaultpage) {
            newFormPageType[2] = FormPageTypeEnum.DefaultFormSubscribedPage;
        } else if (obj.value === DisplayedScreenEnum.Landingpage) {
            newFormPageType[2] = FormPageTypeEnum.LandingPageFormSubscribedPage;
            if (alreadySubscribedLandingPagesList.length) {
                setChosenLandingPage({label: alreadySubscribedLandingPagesList[0]?.Name, value: alreadySubscribedLandingPagesList[0]?.Name, link: alreadySubscribedLandingPagesList[0]?.Link});
                newAlreadySubscribedPage = alreadySubscribedLandingPagesList[0]?.Name;
            }
        } else {
            newFormPageType[2] = FormPageTypeEnum.ExternalWebsiteFormSubscribedPage;
        }
        setExternalWebsite(null);
        resetError();
        dispatch(ModifyForm({FormPageType: newFormPageType, FormAlreadySubscribedPage: newAlreadySubscribedPage}));
    }

    return (
        <>
            <Divider marginTop={32} marginBottom={32}/>
            <Typography variant='h3'>{t('contacthaspreviouslysubscribed')}</Typography>
            <FormPageDropdownSection
                pageTemplateType={PreviewPageType.AlreadySubscribed}
                idPageDropdown="already-subscribed"
                idExternalWebsiteInput="already-subscribed"
                label={t('displayscreenafterhaspreviouslysubscribed')}
                onDropdownPageClick={changeAlreadySubscribedPage}
                pageType={usePageTypeValue(PreviewPageType.AlreadySubscribed)}
                externalWebsiteValue={externalWebsite}
                setExternalWebsite={setExternalWebsite}
                isExternalWebsiteError={isExternalWebsiteError}
                setExternalWebsiteError={setExternalWebsiteError}
                idLandingPage="already-subscribed"
                chosenLandingPage={chosenLandingPage}
                setChosenLandingPage={setChosenLandingPage}
                landingPageSearch={landingPageSearchAlreadySubmitedForm}
                setLandingPageSearch={setLandingPageSearchAlreadySubmitedForm}
                landingPagesParams={alreadySubscribedPagesParams}
            />
        </>
    );
};
